import type React from 'react';
import { type ReactNode, useCallback, useEffect, useState } from 'react';

import { Tag } from '@cobliteam/ui/components';

import { type Incident, IncidentType } from '@cobliteam/api-lib';

import { useGeocode } from '@cobliteam/hooks';

import '@cobliteam/base-components';

import Icon from '@cobliteam/ui/icons';

import Typography from '@cobliteam/ui/components/Typography';
import {
  TrackingPopUp,
  TrackingPopUpContent,
  TrackingPopUpFooter,
  TrackingPopUpHeader,
  TrackingPopUpHeaderMain,
  TrackingPopUpInfoRowText,
  TrackingPopUpSkeleton,
  TrackingPopUpTitle,
} from '../Tracking/components/Common/TrackingPopUp';
import { type IncidentCluster, IncidentIconComponent } from '../Tracking/components/Map';

import { formatTimestamp } from '../Tracking/utils/date';

export interface IncidentPopupProps {
  incidentOrCluster: Incident | IncidentCluster;
  driverName?: string;
  duration?: number;
  children?: ReactNode;
}

export const IncidentPopup: React.FC<IncidentPopupProps> = ({ incidentOrCluster: incident, driverName, children }) => {
  const { getAddress } = useGeocode();
  const [address, setAddress] = useState('');

  const isCluster = 'incidents' in incident;
  const { type, latitude, longitude, time } = incident;

  const devicePlugged = type === IncidentType.DEVICE_PLUGGED;
  const deviceStatusText = devicePlugged ? 'Conectado em' : 'Desconectado em';
  const title = devicePlugged ? 'Dispositivo conectado' : 'Dispositivo desconectado';

  const handleAddress = useCallback(async () => {
    if (!(latitude && longitude)) return;

    const latLng = { lat: latitude, lng: longitude };
    const responseAddress = await getAddress(latLng);
    setAddress(responseAddress);
  }, [latitude, longitude, getAddress]);

  useEffect(() => {
    handleAddress();
  }, [handleAddress]);

  return (
    <TrackingPopUp>
      <TrackingPopUpHeader>
        <TrackingPopUpHeaderMain>
          <IncidentIconComponent type={incident.type} />
          <TrackingPopUpTitle>{title}</TrackingPopUpTitle>
        </TrackingPopUpHeaderMain>
        {isCluster && <Tag icon={<Icon name='triangle-alert' />} color='warning' label={`${incident.incidents.length} eventos`} />}
      </TrackingPopUpHeader>

      <TrackingPopUpContent>
        {isCluster && (
          <div className='flex items-center gap-1.5'>
            <Icon name='triangle-alert' color='warning' />
            <Typography variant='caption-default'>Veja se o dispositivo está bem conectado à sua entrada</Typography>
          </div>
        )}
        <TrackingPopUpInfoRowText title={deviceStatusText}>{formatTimestamp(time)}</TrackingPopUpInfoRowText>
        <TrackingPopUpInfoRowText title='Endereço'>{address || <TrackingPopUpSkeleton />}</TrackingPopUpInfoRowText>
        {driverName && <TrackingPopUpInfoRowText title='Motorista'>{driverName}</TrackingPopUpInfoRowText>}

        <TrackingPopUpFooter>{children}</TrackingPopUpFooter>
      </TrackingPopUpContent>
    </TrackingPopUp>
  );
};
